/**
 * KAN-951: "Помощь" (/help, /help/:slug): sections of the cabinet, each with
 * the whole video and its text; "Смотреть всё подряд" and search.
 * Data: src/tutorials/content.ts; logic: useHelpPlayer / useHelpSearch.
 */
import { useMemo } from 'react';
import { ListVideo, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import {
  helpItemsForRole,
  helpRoleForUser,
  stepsForRole,
  videoUrlFor,
} from '@/tutorials/content';
import { HelpSectionList } from './help/HelpSectionList';
import { HelpVideo } from './help/HelpVideo';
import { useHelpPlayer } from './help/useHelpPlayer';
import { useHelpSearch } from './help/useHelpSearch';

export function HelpPage() {
  const { data: user } = useCurrentUser();
  const role = helpRoleForUser(user);
  const items = useMemo(() => helpItemsForRole(role), [role]);
  const { query, setQuery, results } = useHelpSearch(items, role);
  const { current, playAll, togglePlayAll, onVideoEnded, onVideoFailed, videoRef } = useHelpPlayer(
    items,
    { ready: user !== undefined }
  );
  const steps = current ? stepsForRole(current, role) : [];

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Помощь</h1>
          <p className="mt-1 text-sm text-gray-500">
            Короткие видео по разделам кабинета и текст к каждому из них.
          </p>
        </div>
        <Button
          type="button"
          variant={playAll ? 'default' : 'outline'}
          aria-pressed={playAll}
          onClick={togglePlayAll}
          disabled={items.length === 0}
          className="gap-2 self-start sm:self-auto"
        >
          <ListVideo aria-hidden="true" className="h-4 w-4" />
          Смотреть всё подряд
        </Button>
      </div>

      {items.length === 0 ? (
        <p className="rounded-lg border border-gray-200 bg-white px-4 py-10 text-center text-sm text-gray-500">
          Видео для справки скоро появятся.
        </p>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
          <aside className="space-y-3 lg:sticky lg:top-0 lg:self-start">
            <div className="relative">
              <Search
                aria-hidden="true"
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
              />
              <Input
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Поиск по справке"
                aria-label="Поиск по справке"
                className="pl-9"
              />
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-2">
              <HelpSectionList items={results} currentSlug={current?.slug} />
            </div>
          </aside>

          {current && (
            <section className="min-w-0 space-y-4 rounded-lg border border-gray-200 bg-white p-4 sm:p-6">
              <h2 className="text-xl font-semibold text-gray-900">{current.title}</h2>
              <HelpVideo
                key={current.slug}
                ref={videoRef}
                src={videoUrlFor(current)}
                title={current.title}
                onEnded={onVideoEnded}
                onFailed={onVideoFailed}
              />
              <div className="space-y-4 text-sm leading-relaxed text-gray-700">
                {current.intro && <p>{current.intro}</p>}
                {steps.length > 0 && (
                  <ol className="list-decimal space-y-2 pl-5 marker:font-semibold marker:text-blue-600">
                    {steps.map((step, index) => (
                      <li key={index} className="pl-1">
                        {step.say}
                      </li>
                    ))}
                  </ol>
                )}
                {current.outro && <p className="text-gray-600">{current.outro}</p>}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
